/**
 * GECICI: API/Postgres kaydini ayni ID ile Firebase'e push (varsa uzerine yazar).
 * Layout + Event (+ stock + eventLayout + base layout).
 * + Live ticket/seatSelections push (koltuk ops).
 */
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../database/firebase';
import { getTicketSeats, normalizeSeatSelections } from '../utils/seatConflictUtils';

const ACTOR = 'panel-firebase-push-temp';

const omitUndefined = (obj) => {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
};

const toFirestoreDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Timestamp) return value;
  if (typeof value?.toDate === 'function') return value;
  if (typeof value === 'object' && value.seconds != null) {
    return new Timestamp(value.seconds, value.nanoseconds || 0);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
};

const serializeLayoutField = (layout) => {
  if (layout == null) return null;
  if (typeof layout === 'string') return layout;
  try {
    return JSON.stringify(layout);
  } catch {
    return null;
  }
};

async function upsertDoc(collectionName, id, payload) {
  const ref = doc(db, collectionName, id);
  const existing = await getDoc(ref);
  const base = omitUndefined({
    ...payload,
    id,
    updatedby: ACTOR,
    lastupdate: serverTimestamp(),
  });
  if (!existing.exists()) {
    base.createdby = ACTOR;
    base.createdate = serverTimestamp();
  }
  await setDoc(ref, base, { merge: true });
  return { id, collection: collectionName, existed: existing.exists() };
}

/**
 * Canli web cogu zaman stocks/{id}.seatSelections okur.
 * Nested alan: updateDoc + noktali path (setDoc merge nested path genislemez).
 */
async function mergeSeatIntoFirebaseStock(stockId, seatId, seatEntry) {
  const stockRef = doc(db, 'stocks', stockId);
  const stockSnap = await getDoc(stockRef);

  if (!stockSnap.exists()) {
    await setDoc(stockRef, omitUndefined({
      id: stockId,
      seatSelections: { [seatId]: seatEntry },
      createdby: ACTOR,
      createdate: serverTimestamp(),
      updatedby: ACTOR,
      lastupdate: serverTimestamp(),
    }));
    return { id: stockId, collection: 'stocks', seatId, existed: false };
  }

  await updateDoc(stockRef, {
    [`seatSelections.${seatId}`]: seatEntry,
    updatedby: ACTOR,
    lastupdate: serverTimestamp(),
  });
  return { id: stockId, collection: 'stocks', seatId, existed: true };
}

async function mergeSeatIntoFirebaseSeatSelectionsDoc(stockId, seatId, seatEntry) {
  const selRef = doc(db, 'seatSelections', stockId);
  const selSnap = await getDoc(selRef);
  const selPayload = omitUndefined({
    [seatId]: seatEntry,
    id: stockId,
    updatedby: ACTOR,
    lastupdate: serverTimestamp(),
  });
  if (!selSnap.exists()) {
    selPayload.createdby = ACTOR;
    selPayload.createdate = serverTimestamp();
  }
  await setDoc(selRef, selPayload, { merge: true });
  return {
    id: stockId,
    collection: 'seatSelections',
    seatId,
    existed: selSnap.exists(),
  };
}

/**
 * Layout kaydini Firebase layouts/{id} icine yazar (ayni id, overwrite/merge).
 */
export async function pushLayoutToFirebase(layoutRecord) {
  if (!layoutRecord?.id) throw new Error('Layout id yok');
  const layoutStr = serializeLayoutField(layoutRecord.layout);
  if (!layoutStr) throw new Error('Layout JSON bos');

  return upsertDoc('layouts', layoutRecord.id, {
    name: layoutRecord.name || null,
    venue: layoutRecord.venue || null,
    layoutType: layoutRecord.layoutType || 'seating',
    layout: layoutStr,
    notify: true,
  });
}

function packTicketForFirebase(ticket) {
  const seats = getTicketSeats(ticket);
  const {
    extras,
    createdMs,
    createdate,
    lastupdate,
    createdby,
    updatedby,
    ...rest
  } = ticket || {};
  const extrasObj = extras && typeof extras === 'object' ? { ...extras } : {};
  delete extrasObj.seats;
  return omitUndefined({
    ...extrasObj,
    ...rest,
    id: ticket.id,
    seats: seats.length ? seats : rest.seats || null,
    created:
      typeof ticket.created === 'number'
        ? ticket.created
        : Number(createdMs) || Date.now(),
  });
}

/**
 * Event + stocks + eventLayout (+ varsa base layout) Firebase'e push.
 */
export async function pushEventToFirebase(eventRecord, dataProvider) {
  if (!eventRecord?.id) throw new Error('Event id yok');
  if (!dataProvider) throw new Error('dataProvider gerekli');

  const eventId = eventRecord.id;
  const results = [];

  if (eventRecord.layoutID) {
    try {
      const { data: layout } = await dataProvider.getOne('layouts', { id: eventRecord.layoutID });
      if (layout?.layout) {
        results.push(await pushLayoutToFirebase(layout));
      }
    } catch (e) {
      console.warn('Layout push atlandi:', e?.message || e);
    }
  }

  const eventLayoutId = eventRecord.layoutJSONID || eventRecord.layoutID;
  if (eventLayoutId) {
    try {
      const { data: el } = await dataProvider.getOne('eventLayouts', { id: eventLayoutId });
      if (el) {
        results.push(
          await upsertDoc('eventLayouts', el.id, {
            event: el.event || eventId,
            layoutJSON: el.layoutJSON ?? null,
            extras: el.extras ?? null,
          }),
        );
      }
    } catch (e) {
      console.warn('EventLayout push atlandi:', e?.message || e);
    }
  }

  const { data: stocks } = await dataProvider.getList('stocks', {
    filter: { event: eventId },
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'id', order: 'ASC' },
  });

  for (const stock of stocks || []) {
    let seatSelections = stock.seatSelections ?? null;
    try {
      const { data: sel } = await dataProvider.getOne('seatSelections', { id: stock.id });
      const body = normalizeSeatSelections(sel);
      if (Object.keys(body).length) seatSelections = body;
    } catch {
      // keep
    }

    results.push(
      await upsertDoc('stocks', stock.id, {
        stockID: stock.stockID || stock.id,
        event: stock.event || eventId,
        date: stock.date || null,
        time: stock.time || null,
        type: stock.type ?? eventRecord.type ?? 1,
        stock: stock.stock ?? null,
        sales: stock.sales ?? [],
        isActive: stock.isActive !== false,
        seatSelections,
        updatedate: stock.updatedate ? toFirestoreDate(stock.updatedate) : null,
      }),
    );

    if (seatSelections && typeof seatSelections === 'object') {
      results.push(await upsertDoc('seatSelections', stock.id, seatSelections));
    }
  }

  const eventPayload = omitUndefined({
    name: eventRecord.name,
    description: eventRecord.description ?? null,
    details: eventRecord.details ?? null,
    category: eventRecord.category ?? null,
    subcategories: eventRecord.subcategories ?? null,
    type: eventRecord.type ?? 1,
    city: eventRecord.city ?? null,
    venue: eventRecord.venue ?? null,
    startdate: toFirestoreDate(eventRecord.startdate),
    enddate: toFirestoreDate(eventRecord.enddate),
    duration: eventRecord.duration ?? 0,
    dateType: eventRecord.dateType || 'oneoff',
    sessions: eventRecord.sessions ?? null,
    paymentMethods: eventRecord.paymentMethods ?? null,
    coupons: eventRecord.coupons ?? null,
    rules: eventRecord.rules ?? null,
    badge: eventRecord.badge ?? null,
    banner: eventRecord.banner ?? null,
    bannerCard: eventRecord.bannerCard ?? null,
    venueLayout: eventRecord.venueLayout ?? null,
    videoURL: eventRecord.videoURL ?? null,
    commissionFee: eventRecord.commissionFee ?? 0,
    servicefee: eventRecord.servicefee ?? 0,
    layoutID: eventRecord.layoutID ?? null,
    layoutJSONID: eventRecord.layoutJSONID ?? null,
    organisationCompanies: eventRecord.organisationCompanies ?? null,
    isActive: eventRecord.isActive !== false,
    isVerified: eventRecord.isVerified === true,
    isCancelled: eventRecord.isCancelled === true,
    isSoldOut: eventRecord.isSoldOut === true,
    notify: true,
  });

  results.push(await upsertDoc('events-v2', eventId, eventPayload));

  return {
    eventId,
    pushed: results,
  };
}

/**
 * Tek koltuk: Postgres seatSelections → Firebase (seatSelections doc + stocks.seatSelections).
 */
export async function pushSeatLiveStateToFirebase({
  eventId,
  stockId,
  seatId,
  dataProvider,
}) {
  return pushSeatsLiveStateToFirebase({
    eventId,
    stockId,
    seatIds: [seatId],
    dataProvider,
  });
}

/**
 * Birden fazla koltuk: her biri Zorla 1 gibi iki yere yazilir
 * (seatSelections/{stock} + stocks/{stock}.seatSelections).
 * Toplu "Eksik flag → 1" vb. icin.
 */
export async function pushSeatsLiveStateToFirebase({
  eventId,
  stockId,
  seatIds = [],
  dataProvider,
}) {
  if (!eventId) throw new Error('Event id yok');
  if (!stockId) throw new Error('Stock id yok');
  if (!dataProvider) throw new Error('dataProvider gerekli');

  const uniqueSeats = [
    ...new Set((seatIds || []).map((s) => String(s).trim()).filter(Boolean)),
  ];
  if (!uniqueSeats.length) {
    return {
      eventId,
      stockId,
      pushed: [],
      counts: { tickets: 0, seatSelections: 0, stockSeatPatches: 0 },
    };
  }

  let map = {};
  try {
    const { data: sel } = await dataProvider.getOne('seatSelections', { id: stockId });
    map = normalizeSeatSelections(sel);
  } catch {
    map = {};
  }

  const nowSec = String(Math.floor(Date.now() / 1000));
  const pushed = [];
  for (const seatId of uniqueSeats) {
    const seatEntry = map[seatId] || {
      status: 0,
      statusChangedAt: nowSec,
      user: null,
    };
    pushed.push(await mergeSeatIntoFirebaseSeatSelectionsDoc(stockId, seatId, seatEntry));
    pushed.push(await mergeSeatIntoFirebaseStock(stockId, seatId, seatEntry));
  }

  return {
    eventId,
    stockId,
    seatIds: uniqueSeats,
    pushed,
    counts: {
      tickets: 0,
      seatSelections: uniqueSeats.length,
      stockSeatPatches: uniqueSeats.length,
    },
  };
}

/**
 * Tek bilet + istenen koltuklarin seatSelections entry'leri → Firebase.
 * Mevcut bilete koltuk ekleme / koltuk degistir icin.
 */
export async function pushTicketAndSeatsToFirebase({
  eventId,
  stockId,
  ticketId,
  seatIds = [],
  dataProvider,
}) {
  if (!eventId) throw new Error('Event id yok');
  if (!ticketId) throw new Error('Ticket id yok');
  if (!dataProvider) throw new Error('dataProvider gerekli');

  const results = [];
  const { data: ticket } = await dataProvider.getOne('tickets', { id: ticketId });
  if (!ticket?.id) throw new Error(`Ticket bulunamadı: ${ticketId}`);

  results.push(await upsertDoc('tickets', ticket.id, packTicketForFirebase(ticket)));

  const uniqueSeats = [...new Set((seatIds || []).map((s) => String(s).trim()).filter(Boolean))];
  if (stockId && uniqueSeats.length) {
    let map = {};
    try {
      const { data: sel } = await dataProvider.getOne('seatSelections', { id: stockId });
      map = normalizeSeatSelections(sel);
    } catch {
      map = {};
    }

    for (const seatId of uniqueSeats) {
      const seatEntry =
        map[seatId] || {
          status: 0,
          statusChangedAt: String(Math.floor(Date.now() / 1000)),
          user: null,
        };
      results.push(await mergeSeatIntoFirebaseSeatSelectionsDoc(stockId, seatId, seatEntry));
      results.push(await mergeSeatIntoFirebaseStock(stockId, seatId, seatEntry));
    }
  }

  return {
    eventId,
    stockId,
    ticketId,
    pushed: results,
    counts: { tickets: 1, seatSelections: uniqueSeats.length },
  };
}

/**
 * Koltuk ops: tickets + seatSelections (+ stock.seatSelections) anlik Firebase push.
 */
export async function pushEventLiveStateToFirebase(eventId, dataProvider) {
  if (!eventId) throw new Error('Event id yok');
  if (!dataProvider) throw new Error('dataProvider gerekli');

  const results = [];

  const { data: stocks } = await dataProvider.getList('stocks', {
    filter: { event: eventId },
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'id', order: 'ASC' },
  });

  for (const stock of stocks || []) {
    let seatSelections = null;
    try {
      const { data: sel } = await dataProvider.getOne('seatSelections', { id: stock.id });
      seatSelections = normalizeSeatSelections(sel);
    } catch {
      seatSelections = null;
    }

    results.push(
      await upsertDoc('stocks', stock.id, {
        stockID: stock.stockID || stock.id,
        event: stock.event || eventId,
        date: stock.date || null,
        time: stock.time || null,
        type: stock.type ?? 3,
        stock: stock.stock ?? null,
        sales: stock.sales ?? [],
        isActive: stock.isActive !== false,
        // Full replace — canli web stocks.seatSelections okur
        seatSelections:
          seatSelections && Object.keys(seatSelections).length ? seatSelections : null,
      }),
    );

    if (seatSelections && Object.keys(seatSelections).length) {
      results.push(await upsertDoc('seatSelections', stock.id, seatSelections));
    }
  }

  const { data: tickets } = await dataProvider.getList('tickets', {
    filter: { 'event.id': eventId },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: 'created', order: 'DESC' },
  });

  for (const ticket of tickets || []) {
    results.push(await upsertDoc('tickets', ticket.id, packTicketForFirebase(ticket)));
  }

  return {
    eventId,
    pushed: results,
    counts: {
      stocks: (stocks || []).length,
      tickets: (tickets || []).length,
    },
  };
}

/** react-pdf Html karmasik markup'ta patliyor — details'i sadece <p> yap. */
const stripHtmlToText = (html) =>
  String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\n+/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .trim();

const toSafeParagraphHtml = (text) => {
  if (!text) return '<p></p>';
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<p>${l.replace(/</g, '').replace(/>/g, '')}</p>`)
    .join('');
};

/**
 * Canli bilet PDF acilsin diye events-v2/{eventId}.details HTML'ini sadeleştirir.
 * Sadece Firebase events-v2 (web oradan okur).
 */
export async function fixEventDetailsForTicketPrint(eventId) {
  if (!eventId) throw new Error('eventId gerekli');
  const ref = doc(db, 'events-v2', eventId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error(`Firebase events-v2/${eventId} yok`);
  }
  const data = snap.data() || {};
  const beforeTr = String(data.details?.tr || '');
  const beforeEn = String(data.details?.en || '');
  const safeDetails = {
    tr: toSafeParagraphHtml(stripHtmlToText(beforeTr)),
    en: toSafeParagraphHtml(stripHtmlToText(beforeEn)),
  };

  await updateDoc(ref, {
    details: safeDetails,
    updatedby: ACTOR,
    lastupdate: serverTimestamp(),
  });

  return {
    eventId,
    beforeLen: { tr: beforeTr.length, en: beforeEn.length },
    afterLen: { tr: safeDetails.tr.length, en: safeDetails.en.length },
    hadRiskyMarkup:
      /<em|&nbsp;|<span|<div|<ul|<ol|<li|<strong|<b\b/i.test(beforeTr) ||
      /<em|&nbsp;|<span|<div|<ul|<ol|<li|<strong|<b\b/i.test(beforeEn),
  };
}
